import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import RedlinkList from './RedlinkList';
import RedlinkEditor from './RedlinkEditor';
import RedlinkEvaluationTab from './RedlinkEvaluationTab';

const TAB_NAMES = ['list', 'editor', 'evaluation'] as const;
type TabName = (typeof TAB_NAMES)[number];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`newsletter-redlinks-tabpanel-${index}`}
      aria-labelledby={`newsletter-redlinks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const NewsletterRedlinks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive tab state from URL
  const tabParam = searchParams.get('tab') || 'list';
  const tabName = TAB_NAMES.includes(tabParam as TabName) ? (tabParam as TabName) : 'list';
  const activeTab = TAB_NAMES.indexOf(tabName);
  const editingRedlinkId =
    tabName === 'editor'
      ? searchParams.get('id')
        ? parseInt(searchParams.get('id')!, 10)
        : null
      : null;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTabName = TAB_NAMES[newValue];
    const params = new URLSearchParams();
    params.set('tab', newTabName);
    setSearchParams(params);
  };

  const handleEditRedlink = (redlinkId: number) => {
    setSearchParams({ tab: 'editor', id: String(redlinkId) });
  };

  const handleNewRedlink = () => {
    setSearchParams({ tab: 'editor' });
  };

  const handleRedlinkSaved = () => {
    setSearchParams({ tab: 'list' });
  };

  return (
    <Box m="20px">
      <Header
        title="Newsletter Redlinks"
        subtitle="Redaktionelle Anzeigen im täglichen Newsletter"
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="newsletter redlinks tabs">
          <Tab label="Redlinks" />
          <Tab label={editingRedlinkId ? 'Redlink bearbeiten' : 'Neuer Redlink'} />
          <Tab label="Auswertung" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <RedlinkList onEdit={handleEditRedlink} onNew={handleNewRedlink} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <RedlinkEditor
          redlinkId={editingRedlinkId}
          onSaved={handleRedlinkSaved}
          onCancel={() => setSearchParams({ tab: 'list' })}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <RedlinkEvaluationTab />
      </TabPanel>
    </Box>
  );
};

export default NewsletterRedlinks;
